import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ShoppingBag } from "lucide-react";
import { featuredProducts, SHOP_ALL_URL, checkoutUrl } from "@/data/products";
import { useLiveCutout } from "@/lib/liveCutout";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Show the bundled cutout instantly, then upgrade to the live (auto-updated, background-removed)
// Shopify image once it has successfully loaded. If the live one fails, we simply keep the bundled one.
const ProductImage = ({ p }) => {
  const { src } = useLiveCutout(p.handle, p.image);
  return (
    <img
      src={src}
      alt={`${p.title} — ${p.category} by Alpha Omega Vitality`}
      data-testid={`product-image-${p.id}`}
      loading="eager"
      decoding="async"
      className="relative h-full w-full object-contain p-1 drop-shadow-[0_25px_35px_rgba(26,31,27,0.22)] transition-transform duration-700 ease-out group-hover:scale-[1.07]"
    />
  );
};

const Badge = ({ label }) => {
  const isNew = label === "New";
  return (
    <span
      data-testid="product-badge"
      className={`mb-3 inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] shadow-sm ${
        isNew
          ? "bg-[#1a1f1b] text-[#f4f1eb]"
          : "bg-[#e85d04] text-white"
      }`}
    >
      {!isNew && <span className="h-1.5 w-1.5 rounded-full bg-[#ffd27a]" />}
      {label}
    </span>
  );
};

const ProductCard = ({ p, index, livePrice }) => {
  const price = livePrice || p.price;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.08 }}
      className="group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-[#1a1f1b]/10 bg-[#eae5dc] p-6 sm:p-8 md:col-span-4"
    >
      {p.badge && <Badge label={p.badge} />}

      {/* Whole-card link → Shopify product page (stretched, sits under interactive controls) */}
      <a
        href={p.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`View ${p.title} on the store`}
        data-testid={`product-card-${p.id}`}
        className="absolute inset-0 z-0"
      />

      <div className="relative z-10 pointer-events-none flex items-start justify-between">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#e85d04]">
            {p.category}
          </span>
          <h3 className="font-serif-display text-3xl sm:text-4xl tracking-tight text-[#1a1f1b] mt-2">
            {p.title}
          </h3>
        </div>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#1a1f1b]/20 text-[#1a1f1b] transition-colors duration-300 group-hover:bg-[#e85d04] group-hover:border-[#e85d04] group-hover:text-white">
          <ArrowUpRight size={18} />
        </span>
      </div>

      <div className="relative z-0 pointer-events-none my-6 aspect-square">
        <ProductImage p={p} />
      </div>

      <div className="relative z-10 flex items-end justify-between gap-4">
        <a
          href={checkoutUrl(p.variantId)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`add-to-cart-${p.id}`}
          onClick={(e) => e.stopPropagation()}
          className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-[#1a1f1b] px-4 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-[#f4f1eb] transition-colors duration-300 hover:bg-[#e85d04]"
        >
          <ShoppingBag size={15} />
          Add to cart
        </a>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4a524c]">From</div>
          <div data-testid={`product-price-${p.id}`} className="font-serif-display text-2xl text-[#1a1f1b]">
            ${price}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const FeaturedProducts = () => {
  const [livePrices, setLivePrices] = useState({});

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/shop/prices`);
        if (!res.ok) return;
        const data = await res.json();
        if (active && data?.prices) {
          const map = {};
          Object.entries(data.prices).forEach(([handle, v]) => {
            if (v?.price) map[handle] = v.price;
          });
          setLivePrices(map);
        }
      } catch (_) {
        /* fall back to static prices */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <section id="products" data-testid="products-section" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[#e85d04]">
              <span className="h-px w-8 bg-[#c79a3e]" />
              The Collection
            </span>
            <h2 className="font-serif-display text-5xl sm:text-6xl tracking-tight text-[#1a1f1b] mt-3 max-w-xl leading-[0.95]">
              Formulas worth the ritual
            </h2>
          </div>
          <a
            href={SHOP_ALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="products-shopall-btn"
            className="group inline-flex items-center gap-2 text-sm text-[#1a1f1b] border-b border-[#1a1f1b]/30 pb-1 hover:border-[#e85d04] hover:text-[#e85d04] transition-colors duration-300 w-fit"
          >
            View all 30+ products
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-auto">
          {featuredProducts.map((p, i) => (
            <ProductCard key={p.id} p={p} index={i} livePrice={livePrices[p.handle]} />
          ))}
        </div>
      </div>
    </section>
  );
};
